// FILE: MessageSelectionToolbar.tsx
// Purpose: Floating actions offered for the text currently selected in the transcript.
// Layer: Web chat presentation
// Exports: MessageSelectionToolbar

import { MessageIcon, PanelRightIcon } from "~/lib/icons";

interface MessageSelectionToolbarProps {
  position: { x: number; y: number };
  onReply: () => void;
  /**
   * Absent when no agent is enabled for side chats, which is what disables the
   * action rather than hiding it: an action that appears and disappears with
   * configuration the user cannot see reads as a bug.
   */
  onStartSideChat?: (() => void) | undefined;
  sideChatDisabledReason?: string | undefined;
}

export function MessageSelectionToolbar(props: MessageSelectionToolbarProps) {
  const { position, onReply, onStartSideChat, sideChatDisabledReason } = props;

  return (
    <div
      // Fixed rather than absolute: the anchor comes from getBoundingClientRect on the selection,
      // which is already in viewport coordinates, and the transcript scrolls under it.
      className="fixed z-50 flex items-center gap-0.5 rounded-md border border-border/60 bg-popover p-0.5 text-popover-foreground shadow-md"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      data-testid="message-selection-toolbar"
      // The toolbar must not steal the selection it acts on: a mousedown here would collapse it
      // before the click handler ever runs.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 rounded-[4px] px-2 py-1 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
        onClick={onReply}
      >
        <MessageIcon className="size-3.5 opacity-70" />
        Reply
      </button>
      <button
        type="button"
        className="flex items-center gap-1.5 rounded-[4px] px-2 py-1 text-xs font-medium hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40"
        disabled={!onStartSideChat}
        {...(sideChatDisabledReason ? { title: sideChatDisabledReason } : {})}
        onClick={onStartSideChat}
      >
        <PanelRightIcon className="size-3.5 opacity-70" />
        Side chat
      </button>
    </div>
  );
}
